import { Activity, Dot, Languages, Loader2, ChevronDown } from "lucide-react";
import React, { useState, useEffect, useLayoutEffect, use, useRef } from "react";
import { useNavigate } from "react-router";
import { getContactInitialsClassName } from '../utils/colorUtils';
import { formatValueByType } from '../utils/formatUtils';
import {
    Contact,
    ContactList,
    Text,
    NumberInput,
    DateInput,
    TimeInput,
    Dropdown,
    MultiSelect,
    Boolean,
    Subheader,
    Instructions,
    FileUpload,
    MultiFile,
    Calculation,
    DocGen,
    Deadline,
    SearchSelect
} from "./FieldComponents";
import { fetchUsers } from "./CreateLead";

export const EditableVital = ({
    label,
    value,
    field,
    table,
    updateVital,
    formatDate,
    originalValue = null,
    options = [],
    typeId,
    allFields = [],
    fieldUpdates = [],
    lead_id = null,
    sectionName = '',
    contactName = null,
    users,
    onCreateNewContact,
    inCaseHeader = false,
    customVitalName = null,
    vitalKey = null,
    activeVitalKey = null,
    setActiveVitalKey = null
}) => {
    const [editing, setEditing] = useState(false);
    const [inputValue, setInputValue] = useState(value || "");
    const [loading, setLoading] = useState(false);
    const [elementPosition, setElementPosition] = useState({ top: 0, left: 0 });
    const [fetchedUsers, setFetchedUsers] = useState([]);
    const elementRef = useRef(null);
    const editorRef = useRef(null);
    const isDeadlineVital = Number(typeId) === 20;

    const parseDeadlineValue = (rawValue) => {
        if (!rawValue) return { due: '', done: '' };

        if (typeof rawValue === 'object') {
            return {
                due: rawValue.due || rawValue.date || rawValue.value || '',
                done: rawValue.done || ''
            };
        }

        const rawString = String(rawValue).trim();
        if (!rawString) return { due: '', done: '' };

        try {
            const parsed = JSON.parse(rawString);
            const parsedValue = Array.isArray(parsed) ? parsed[0] : parsed;
            if (parsedValue && typeof parsedValue === 'object') {
                return parseDeadlineValue(parsedValue);
            }
        } catch {
        }

        return { due: rawString, done: '' };
    };

    const serializeDeadlineValue = (deadlineValue) => JSON.stringify({
        due: deadlineValue?.due || '',
        done: deadlineValue?.done || ''
    });

    const normalizeValue = (val) => {
        if (val === null || val === undefined) return "";
        if (Array.isArray(val)) return val.map((item) => (item === null || item === undefined ? "" : String(item)));
        if (typeof val === "object") return JSON.stringify(val);
        return String(val);
    };

    const parsedDeadlineValue = isDeadlineVital ? parseDeadlineValue(inputValue) : { due: '', done: '' };
    const deadlineHasDoneValue = Boolean(parsedDeadlineValue.done);

    const isSameValue = (nextValue, currentValue) => {
        const normalizedNext = normalizeValue(nextValue);
        const normalizedCurrent = normalizeValue(currentValue);

        if (Array.isArray(normalizedNext) || Array.isArray(normalizedCurrent)) {
            if (!Array.isArray(normalizedNext) || !Array.isArray(normalizedCurrent)) return false;
            if (normalizedNext.length !== normalizedCurrent.length) return false;
            return normalizedNext.every((val, idx) => val === normalizedCurrent[idx]);
        }

        return normalizedNext === normalizedCurrent;
    };

    useEffect(() => {
        setInputValue(value || "");
    }, [value]);

    useEffect(() => {
        if (!editing) return;
        if (!activeVitalKey || !vitalKey) return;
        if (activeVitalKey !== vitalKey) {
            setEditing(false);
        }
    }, [activeVitalKey, editing, vitalKey]);

    // Fixed overlays use viewport coordinates and the rendered editor's size,
    // not the (usually much narrower) vital that opened them.
    useLayoutEffect(() => {
        if (!editing || !inCaseHeader) return;

        const updatePosition = () => {
            if (!elementRef.current || !editorRef.current) return;
            const rect = elementRef.current.getBoundingClientRect();
            const editorRect = editorRef.current.getBoundingClientRect();
            const minMargin = 5;
            const viewportWidth = document.documentElement.clientWidth || window.innerWidth;
            const viewportHeight = window.innerHeight;
            const left = Math.max(minMargin, Math.min(
                rect.left, viewportWidth - editorRect.width - minMargin
            ));
            const top = Math.max(minMargin, Math.min(
                rect.bottom, viewportHeight - editorRect.height - minMargin
            ));
            setElementPosition((previous) => {
                if (previous.top === top && previous.left === left) return previous;
                return { top, left };
            });
        };

        updatePosition();
        window.addEventListener('resize', updatePosition);
        // Capture scroll events from the horizontally scrollable vitals row.
        window.addEventListener('scroll', updatePosition, true);
        const observer = typeof ResizeObserver !== 'undefined'
            ? new ResizeObserver(updatePosition)
            : null;
        if (editorRef.current) observer?.observe(editorRef.current);
        if (elementRef.current) observer?.observe(elementRef.current);
        return () => {
            window.removeEventListener('resize', updatePosition);
            window.removeEventListener('scroll', updatePosition, true);
            observer?.disconnect();
        };
    }, [editing, inCaseHeader, loading, parsedDeadlineValue.done]);

    useEffect(() => {
        if (editing && inCaseHeader) {
            let selectFocusTimeout;
            // Auto-focus after position is calculated - SCOPED to only the fixed form
            const focusTimeout = setTimeout(() => {
                const fixedForm = editorRef.current;
                if (fixedForm) {
                    const inputs = fixedForm.querySelectorAll('.vital-input, input, select, textarea');
                    const lastInput = inputs[inputs.length - 1];
                    if (lastInput && typeof lastInput.focus === 'function') {
                        // For select elements, delay focus slightly more to avoid conflicts
                        if (lastInput.tagName === 'SELECT') {
                            selectFocusTimeout = setTimeout(() => lastInput.focus(), 100);
                        } else {
                            lastInput.focus();
                        }
                    }
                }
            }, 10);
            return () => {
                clearTimeout(focusTimeout);
                clearTimeout(selectFocusTimeout);
            };
        }
    }, [editing, inCaseHeader]);

    useEffect(() => {
        if (!editing || inCaseHeader) return;

        const timeoutId = setTimeout(() => {
            const container = elementRef.current;
            if (!container) return;
            const focusTarget = container.querySelector(
                '.vital-input, input, select, textarea, [tabindex]:not([tabindex="-1"])'
            );
            if (focusTarget && typeof focusTarget.focus === 'function') {
                focusTarget.focus();
            }
        }, 0);

        return () => {
            clearTimeout(timeoutId);
        };
    }, [editing, inCaseHeader, typeId]);

    useEffect(() => {
        if (editing && inCaseHeader && typeId !== 8 && typeId !== 9) {
            // Only apply aggressive click-outside behavior for non-contact fields
            const handleClickOutside = (event) => {
                const editForm = document.querySelector('.form-group.nm[style*="position: fixed"]');
                const isSelectElement = event.target.tagName === 'SELECT' || event.target.closest('select');
                
                // More comprehensive dropdown detection for non-contact fields
                const isDropdown = event.target.closest('.search-select') || 
                                 event.target.closest('.dropdown-option') ||
                                 event.target.classList.contains('dropdown-option');
                
                if (editForm && !editForm.contains(event.target) && !elementRef.current?.contains(event.target) && !isSelectElement && !isDropdown) {
                    // For dropdown types (typeId 10), don't auto-save on click outside since onChange already saves
                    if (typeId === 10) {
                        setEditing(false);
                    } else {
                        handleBlur(inputValue);
                    }
                }
            };

            document.addEventListener('mousedown', handleClickOutside);
            return () => {
                document.removeEventListener('mousedown', handleClickOutside);
            };
        }
        
        // Special handler for Contact fields (typeId 8) - only close if contact is displayed (not searching)
        if (editing && inCaseHeader && typeId === 8) {
            const handleContactClickOutside = (event) => {
                const editForm = document.querySelector('.form-group.nm[style*="position: fixed"]');
                const contactInput = event.target.closest('.contact-input');
                const contactDropdown = event.target.closest('.contact-search-dropdown');
                const isContactElement = contactInput || contactDropdown;
                
                // Only close if clicking outside AND not interacting with contact components
                if (editForm && !editForm.contains(event.target) && !elementRef.current?.contains(event.target) && !isContactElement) {
                    console.log("🚪 Closing contact vital due to click outside");
                    setEditing(false);
                }
            };

            // Use a small delay to let contact selection complete if in progress
            const timeoutId = setTimeout(() => {
                document.addEventListener('mousedown', handleContactClickOutside);
            }, 100);
            
            return () => {
                clearTimeout(timeoutId);
                document.removeEventListener('mousedown', handleContactClickOutside);
            };
        }
    }, [editing, inCaseHeader, inputValue, typeId]);

    useEffect(() => {
        if (!editing || inCaseHeader || typeId !== 8) return;

        const handleSidebarContactClickOutside = (event) => {
            const isInsideVital = elementRef.current?.contains(event.target);
            const contactDropdown = event.target.closest('.contact-search-dropdown');
            const contactInput = event.target.closest('.contact-input');
            const isContactElement = contactDropdown || contactInput;

            if (!isInsideVital && !isContactElement) {
                setEditing(false);
            }
        };

        document.addEventListener('mousedown', handleSidebarContactClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleSidebarContactClickOutside);
        };
    }, [editing, inCaseHeader, typeId]);

    const handleFieldChange = async (fieldId, newVal) => {
        await handleSave(newVal);
    };

    const handleSave = async (newValue) => {
        if (!isSameValue(newValue, value)) {
            setLoading(true);
            if (updateVital) {
                await updateVital(field, newValue, table, customVitalName);
            }
            setLoading(false);
        }
        setEditing(false);
    };

    const handleBlur = (newValue) => {
        if (inCaseHeader) {
            // Auto-save and exit editing mode on blur when in case header
            handleSave(newValue);
        }
    };

    const fetchUsers = async () => {
        const response = await fetch(`https://api.casedb.co/user.php?time=${new Date().getTime()}`, { credentials: 'include' });
        const data = await response.json();
        if (response.ok && data.users) {
            setFetchedUsers(data.users);
        }
    }

    useEffect(() => {
        if (typeId === 8 || typeId === 9) {
            fetchUsers();
        }
    }, [users]);

    const renderInputByType = () => {
        switch (typeId) {
            case 1:
                return (
                    <Text
                        type="text"
                        placeholder={label}
                        value={inputValue}
                        onChange={(val) => setInputValue(val)}
                        onBlur={() => inCaseHeader ? handleBlur(inputValue) : handleSave(inputValue)}
                        onKeyDown={(e) => e.key === "Enter" && handleSave(inputValue)}
                        autoFocus={inCaseHeader}
                    />
                );
            case 2:
                return (
                    <Text
                        type="textarea"
                        placeholder={label}
                        value={inputValue}
                        onChange={(val) => setInputValue(val)}
                        onBlur={() => inCaseHeader ? handleBlur(inputValue) : handleSave(inputValue)}
                        onKeyDown={(e) => e.key === "Enter" && handleSave(inputValue)}
                        autoFocus={inCaseHeader}
                    />
                );
            case 3:
                return (
                    <NumberInput
                        type="currency"
                        value={inputValue}
                        onChange={(val) => setInputValue(val)}
                        onBlur={() => inCaseHeader ? handleBlur(inputValue) : handleSave(inputValue)}
                        onKeyDown={(e) => e.key === "Enter" && handleSave(inputValue)}
                        autoFocus={inCaseHeader}
                    />
                );
            case 4:
                return (
                    <NumberInput
                        type="percent"
                        value={inputValue}
                        onChange={(val) => setInputValue(val)}
                        onBlur={() => handleSave(inputValue)}
                        onKeyDown={(e) => e.key === "Enter" && handleSave(inputValue)}
                    />
                );
            case 5:
                return (
                    <NumberInput
                        type="number"
                        value={inputValue}
                        onChange={(val) => setInputValue(val)}
                        onBlur={() => inCaseHeader ? handleBlur(inputValue) : handleSave(inputValue)}
                        onKeyDown={(e) => e.key === "Enter" && handleSave(inputValue)}
                        autoFocus={inCaseHeader}
                    />
                );
            case 6:
                return (
                    <DateInput
                        value={String(inputValue)}
                        onChange={(val) => {
                            console.log("Date onChange:", val);
                            setInputValue(val);
                        }}
                        onBlur={(val) => {
                            console.log("Date onBlur - closing edit mode");
                            handleSave(val);
                            if (inCaseHeader) {
                                setEditing(false);
                            }
                        }}
                        autoFocus={inCaseHeader}
                    />
                );
            case 7:
                return (
                    <TimeInput
                        value={inputValue}
                        onChange={(val) => setInputValue(val)}
                        onBlur={() => handleSave(inputValue)}
                    />
                );
            case 8:
                return <Contact 
                            selectedContact={originalValue ?? inputValue} 
                            setSelectedContact={(contact) => {
                                // Handle different contact object structures
                                let contactId = null;
                                if (contact) {
                                    if (typeof contact === 'object') {
                                        contactId = contact.id || contact.contact_id || null;
                                    } else {
                                        contactId = contact; // In case it's already an ID
                                    }
                                }
                                
                                // Only proceed if we have a valid contact ID
                                if (contactId !== null && contactId !== undefined && contactId !== "") {
                                    const currentId = originalValue ?? inputValue ?? value ?? null;
                                    if (isSameValue(contactId, currentId)) {
                                        if (inCaseHeader) {
                                            setEditing(false);
                                        }
                                        return;
                                    }

                                    setInputValue(contactId);

                                    const updateResult = updateVital ? updateVital(field, contactId, table) : null;
                                    Promise.resolve(updateResult)
                                        .then(() => {
                                            if (inCaseHeader) {
                                                setTimeout(() => setEditing(false), 200);
                                            }
                                        })
                                        .catch(error => {
                                            console.error('updateVital failed for contact:', error);
                                            if (inCaseHeader) {
                                                setEditing(false);
                                            }
                                        });
                                }
                            }} 
                            users={label === 'First Primary' ? fetchedUsers : null}
                            // Minimize props to reduce conflicts
                            disabled={false}
                            lead={false}
                            onCreateNewContact={() => {
                                if (onCreateNewContact && typeof onCreateNewContact === 'function') {
                                    onCreateNewContact((newContact) => {
                                        if (newContact && newContact.id) {
                                            const contactId = newContact.id;
                                            const currentId = originalValue ?? inputValue ?? value ?? null;
                                            if (isSameValue(contactId, currentId)) {
                                                if (inCaseHeader) {
                                                    setEditing(false);
                                                }
                                                return;
                                            }
                                            setInputValue(contactId);
                                            const updateResult = updateVital ? updateVital(field, contactId, table) : null;
                                            Promise.resolve(updateResult)
                                                .then(() => {
                                                    if (inCaseHeader) {
                                                        setEditing(false);
                                                    }
                                                })
                                                .catch((error) => {
                                                    console.error('Error saving new contact:', error);
                                                    if (inCaseHeader) {
                                                        setEditing(false);
                                                    }
                                                });
                                        }
                                    });
                                }
                            }}
                        />;
            case 9:
                return <ContactList 
                    value={Array.isArray(inputValue) ? inputValue : []} 
                    onChange={(ids) => {
                        console.log("ContactList onChange:", ids);
                        setInputValue(ids);
                        if (Array.isArray(ids) && ids.length > 0) {
                            updateVital(field, ids, table).then(() => {
                                console.log("Contact list saved successfully");
                                if (inCaseHeader) {
                                    setEditing(false);
                                }
                            }).catch((error) => {
                                console.error('Error saving contact list:', error);
                            });
                        }
                    }}
                    onCreateNewContact={() => {
                        if (onCreateNewContact && typeof onCreateNewContact === 'function') {
                            onCreateNewContact((newContact) => {
                                if (newContact && newContact.id) {
                                    // For ContactList, we need to append to the existing array
                                    const currentIds = Array.isArray(inputValue) ? inputValue : [];
                                    const newIds = [...currentIds, newContact.id];
                                    console.log("New contact added to list:", newIds);
                                    setInputValue(newIds);
                                    updateVital(field, newIds, table).then(() => {
                                        console.log("New contact list saved successfully");
                                        if (inCaseHeader) {
                                            setEditing(false);
                                        }
                                    }).catch((error) => {
                                        console.error('Error saving new contact to list:', error);
                                    });
                                }
                            });
                        }
                    }}
                />;
            case 10:
                return (
                    <div className="filter-group">
                        <div className="select-wrapper" style={{ width: '100%' }}>
                            <select 
                                className='filter-select vital-input'
                                style={{ width: '100%' }}
                                value={String(inputValue || "")}
                                onChange={(e) => {
                                    const selectedValue = e.target.value;
                                    console.log("Select onChange:", { selectedValue, inputValue, options });
                                    setInputValue(selectedValue);
                                    if (inCaseHeader) {
                                        // For case header, save immediately on change and close editing
                                        setTimeout(() => handleSave(selectedValue), 0);
                                    }
                                }}
                                onBlur={(e) => {
                                    // Delay the blur to allow onChange to complete first
                                    setTimeout(() => {
                                        if (!inCaseHeader) {
                                            handleSave(inputValue);
                                        }
                                    }, 100);
                                }}
                                onFocus={(e) => {
                                    console.log("Select focused:", { inputValue, options });
                                    // Prevent immediate close by stopping the event from bubbling
                                    e.stopPropagation();
                                }}
                                onClick={(e) => {
                                    // Prevent immediate close when clicking to open dropdown
                                    e.stopPropagation();
                                }}
                                ref={(selectRef) => {
                                    // Manual focus handling for case header
                                    if (selectRef && inCaseHeader && editing) {
                                        setTimeout(() => {
                                            try {
                                                selectRef.focus();
                                            } catch (err) {
                                                console.log('Focus error:', err);
                                            }
                                        }, 50);
                                    }
                                }}
                            >
                                <option value="">Select...</option>
                                {Array.isArray(options) ? options.map((option, index) => (
                                    <option key={index} value={String(index)}>
                                        {option}
                                    </option>
                                )) : []}
                            </select>
                            <ChevronDown size={16} className="select-chevron" />
                        </div>
                    </div>
                );
            case 11:
                return (
                    <MultiSelect
                        options={options || "[]"} 
                        value={inputValue} 
                        onChange={(val) => setInputValue(val)}
                        onBlur={() => handleSave(inputValue)}
                    />
                );
            case 12:
                return (
                    <Boolean
                        options={options || []} 
                        value={inputValue != undefined ? Number(inputValue) : 2} 
                        onChange={(selectedValue) => setInputValue(selectedValue)}
                        onBlur={() => handleSave(inputValue)}
                    />
                );
            case 13:
                return <Subheader title={label} />;
            case 14:
                return <Instructions instructions={label} />;
            case 15:
                return <FileUpload value={inputValue} onChange={(val) => handleFieldChange(field, val)} lead_id={lead_id} section_name={sectionName} />;
            case 16:
                return <MultiFile value={inputValue} lead_id={lead_id} sectionName={sectionName} onChange={(val) => handleFieldChange(field, val)} />;
            case 17:
                return <Calculation options={options} fieldId={field} lead_id={lead_id} fields={allFields} fieldUpdates={fieldUpdates} onChange={handleSave} />;
            case 18:
                return <DocGen value={inputValue} onChange={(val) => handleFieldChange(field, val)} />;
            case 20:
                const parsedValue = parseDeadlineValue(inputValue);
                return <Deadline value={{ due: parsedValue.due || '', done: parsedValue.done || '' }} title={label} onChange={(updated) => handleFieldChange(field, serializeDeadlineValue({ ...parsedValue, ...updated }))} vital={true} />;
            case 22:
                return (
                    <SearchSelect
                        value={inputValue} 
                        onChange={(val) => setInputValue(val)}
                        onBlur={() => handleSave(inputValue)}
                        options={options} 
                    />
                );
            default:
                return (
                    <input
                        type="text"
                        className='vital-input'
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onBlur={() => inCaseHeader ? handleBlur(inputValue) : handleSave(inputValue)}
                        onKeyDown={(e) => e.key === "Enter" && handleSave(inputValue)}
                        autoFocus={inCaseHeader}
                    />
                );
        }
    };

    return (
        <>
            <div 
                ref={elementRef}
                className='sidebar-vital sidebar' 
                onClick={() => {
                    if (setActiveVitalKey && vitalKey) {
                        setActiveVitalKey(vitalKey);
                    }
                    setEditing(true);
                }} 
            >
                <span className='subtext active' title={label}>{label}:</span>
                {editing && !inCaseHeader ? (
                    loading 
                        ? <Loader2 className="spinner" /> 
                        : (
                            <div 
                                className={`form-group nm ${isDeadlineVital ? 'deadline-vital-editor' : ''}`}
                                onBlur={(e) => {
                                    const related = e.relatedTarget;
                                    const isInside = related && e.currentTarget.contains(related);
                                    const isDropdown = related && (
                                        related.closest?.('.search-select') ||
                                        related.closest?.('.dropdown-option') ||
                                        related.closest?.('.dropdown') ||
                                        related.closest?.('.contact-search-dropdown')
                                    );

                                    if (!isInside && !isDropdown) {
                                        handleSave(inputValue);
                                    }
                                }}
                            >
                                {renderInputByType()}
                            </div>
                        )
                ) : !editing ? (
                    <span className='subtext'>
                        {loading ? <Loader2 className="spinner" /> : (
                            contactName ? contactName : 
                            options.length > 0 ? options[value] : 
                            formatValueByType(value, typeId)
                        )}
                    </span>
                ) : null}
            </div>
            
            {/* Render editing form as fixed positioned overlay for case header */}
            {editing && inCaseHeader && (
                <div 
                    ref={editorRef}
                    className={`form-group nm ${deadlineHasDoneValue ? 'deadline-vital-editor' : ''}`}
                    style={{
                        position: 'fixed',
                        top: `${elementPosition.top}px`,
                        left: `${elementPosition.left}px`,
                        maxWidth: 'min(400px, calc(100vw - 10px))',
                        zIndex: 99999,
                        padding: '8px',
                        border: '1px solid var(--border-color)',
                        borderRadius: '12px',
                        backgroundColor: 'var(--secondary-color)',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                    }}
                    onBlur={(e) => {
                        // Only close if clicking outside the form
                        if (!e.currentTarget.contains(e.relatedTarget)) {
                            handleBlur(inputValue);
                        }
                    }}
                >
                    {loading ? <Loader2 className="spinner" /> : renderInputByType()}
                </div>
            )}
        </>
    );
};

export const CaseSidebar = ({ id, cases, fetchCases, caseTemplates, caseTypes, formatDate, onCreateNewContact }) => {
    const [caseData, setCaseData] = useState(cases.find((c) => c.case_id === id) ?? {});
    const [leadData, setLeadData] = useState(caseData.lead ?? caseData.lead_data ?? {});
    const [leadUpdates, setLeadUpdates] = useState(leadData.lead_updates_data ?? []);
    const [vitals, setVitals] = useState([]);
    const [users, setUsers] = useState([]);
    const [activeVitalKey, setActiveVitalKey] = useState(null);

    const navigate = useNavigate();

    const fetchVitals = async () => {
        try {
            const templateId = caseData?.template_id || caseData?.case?.template_id;
            const leadId = leadData?.id || leadData?.lead_id;
            const caseId = caseData?.case_id || caseData?.case?.case_id || id;
            
            const response = await fetch(`https://api.casedb.co/sections.php?vitals=true&template_id=${templateId}&lead_id=${leadId}&case_id=${caseId}&${new Date().getTime()}`);
            const data = await response.json();
            if (data.success) {
                const currentCaseTypeId = leadData?.case_type_id || caseData?.lead?.case_type_id;
                const filteredVitals = data?.vitals?.filter((vital) => 
                    vital?.case_type_id === null || 
                    String(vital?.case_type_id) === String(currentCaseTypeId)
                ) || [];
                setVitals(filteredVitals);
            }
        } catch (error) {
            console.error("Error fetching vitals:", error);
        }
    };

    useEffect(() => {
        // Update case data when id or cases change
        const newCaseData = cases.find((c) => c.case_id === id) ?? {};
        setCaseData(newCaseData);
        setLeadData(newCaseData.lead ?? newCaseData.lead_data ?? {});
        setLeadUpdates(newCaseData.lead?.lead_updates_data ?? newCaseData.lead_data?.lead_updates_data ?? []);
    }, [id, cases]);

    useEffect(() => {
        const templateId = caseData?.template_id || caseData?.case?.template_id;
        const caseId = caseData?.case_id || caseData?.case?.case_id;
        
        if (templateId && caseId) {
            fetchVitals();
    
            const getUsers = async () => {
                const userList = await fetchUsers();
                if (userList) setUsers(userList);
            };
    
            getUsers();
        }
    }, [caseData, leadData]);

    const updateVital = async (field, newValue, table, customVitalName = null) => {
        const caseId = caseData?.case_id || caseData?.case?.case_id || id;
        const leadId = leadData?.id || leadData?.lead_id;
        const firstPrimaryId = (() => {
            const fp = vitals?.find(v => v.name === "First Primary" || v.field_id === -2);
            return fp?.original_value || null;
        })();
        
        const payload = {
            case_id: caseId,
            lead_id: leadId,
            field,
            value: newValue,
            table
        };

        if (customVitalName) {
            payload.custom_vital_name = customVitalName;
            payload.is_custom_vital = true;
            if (firstPrimaryId) payload.user_id = firstPrimaryId;
        }

        try {
            const response = await fetch("https://api.casedb.co/update-vital.php", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: 'include',
                body: JSON.stringify(payload)
            });

            const result = await response.json();
            if (result.success) {
                if (table === "cases") {
                    setCaseData((prev) => ({ ...prev, [field]: newValue }));
                } else if (table === "leads") {
                    setLeadData((prev) => ({ ...prev, [field]: newValue }));
                } else if (table === "lead_updates") {
                    setLeadUpdates((prev) =>
                        prev.map((item) =>
                            item.field_name === field ? { ...item, value: newValue } : item
                        )
                    );
                }
                fetchCases();
                fetchVitals();
            } else {
                console.error("Update failed:", result.message);
            }
        } catch (error) {
            console.error("Error updating vital:", error);
        }
    };

    return (
        <div className='case-sidebar'>
            <div className='case-sidebar-header' onClick={() => navigate(`/case/${caseData.case_id}`)}>
                {caseData.contact_display && caseData.contact_display.includes('uploads')
                    ? <img className='contact-initials large' src={`https://api.casedb.co/${caseData.contact_display}`} alt="Profile" />
                    : <span className={getContactInitialsClassName(caseData.case_id, 'contact-initials', 'large')}>{caseData.contact_display}</span>
                }
                <div className="case-info">
                    <div className="case-title-row">
                        <span>{caseData.case_name || "Unknown Case"}</span>
                        {caseData.contact_language && caseData.contact_language.toLowerCase() !== 'english' && (
                            <Languages 
                                size={14} 
                                className="language-indicator" 
                                title={`Needs an interpreter for ${caseData.contact_language}`}
                            />
                        )}
                    </div>
                    <div className='case-id subtext'>{caseData.case_id}</div>
                </div>
            </div>
            <div className='case-sidebar-tags'>
                {(JSON.parse(caseData?.tags) && JSON.parse(caseData?.tags)?.length > 0) && (
                    JSON.parse(caseData?.tags).map((tag, index) => (
                        <span key={index} className='tag caps' onClick={() => navigate(`/search?tag=${tag}`)}>
                            {tag.includes("#") ? tag : `#${tag}`}
                        </span>
                    ))
                )}
            </div>

            <div className='case-sidebar-breakdown subtext'>
                {`${caseData?.phase_name ? caseData.phase_name : ''} ${Number(caseData?.phase_id) === 22 ? `/ ${caseData?.subphase_name || 'Unknown'}` : caseData?.subphase_name ? `/ ${caseData.subphase_name}` : ''} ${caseData?.first_primary ? `• ${caseData.first_primary}` : ''} ${caseData?.create_date ? `• Created: ${formatDate(caseData?.create_date)?.[0]}` : ''} ${caseData?.updated_at ? `• Last Activity: ${formatDate(caseData?.updated_at)?.[0]} ${formatDate(caseData?.updated_at)?.[2]}` : ''}`}
            </div>

            <div className="divider horizontal"></div>

            <div className='case-sidebar-vitals'>
                <div className='sidebar-vital-header'><Activity size={16}/>{" "}Vitals</div>
                {/* Only render hardcoded Case Type if it's not already in the vitals array */}
                {!vitals.some(vital => vital.name === "Case Type" || vital.field_id === 10) && (
                    <EditableVital
                        key="case-type-id"
                        vitalKey="case-type-id"
                        activeVitalKey={activeVitalKey}
                        setActiveVitalKey={setActiveVitalKey}
                        label="Case Type"
                        value={(() => {
                            // Convert case type ID to array index for display
                            if (!Array.isArray(caseTypes) || caseTypes.length === 0) {
                                return null;
                            }
                            const currentCaseType = caseTypes.find(ct => ct.id === String(leadData?.case_type_id));
                            if (currentCaseType) {
                                return caseTypes.findIndex(ct => ct.id === currentCaseType.id);
                            }
                            return null;
                        })()}
                        typeId={10}
                        field="case_type_id"
                        table="leads"
                        updateVital={(field, newValue, table) => {
                            // Convert array index back to case type ID
                            if (Array.isArray(caseTypes) && !isNaN(newValue) && caseTypes[newValue]) {
                                updateVital(field, caseTypes[newValue].id, table);
                            } else {
                                updateVital(field, newValue, table);
                            }
                        }}
                        options={Array.isArray(caseTypes) ? caseTypes.map(ct => ct.name) : []}
                        formatDate={formatDate}
                        users={users}
                        onCreateNewContact={(callback) => {
                            if (callback && typeof callback === 'function') {
                                window.contactCreationCallback = callback;
                            }
                            // Note: setCreateContact would need to be passed as prop or handled differently
                        }}
                    />
                )}
                {/* Hardcoded First Primary vital display - always show */}
                <EditableVital
                    key="first-primary-id"
                    vitalKey="first-primary-id"
                    activeVitalKey={activeVitalKey}
                    setActiveVitalKey={setActiveVitalKey}
                    label="First Primary"
                    value={(() => {
                        // Find First Primary in vitals array
                        const firstPrimaryVital = vitals?.find(vital => vital.name === "First Primary");
                        return firstPrimaryVital ? firstPrimaryVital.value : null;
                    })()}
                    originalValue={vitals?.find(vital => vital.name === "First Primary")?.original_value}
                    typeId={8}
                    field="first_primary_id"
                    table="cases"
                    updateVital={() => {}} // Non-editable for now
                    options={[]}
                    formatDate={formatDate}
                    users={users}
                />
                {/* Filter and deduplicate vitals array before mapping */}
                {(() => {
                    // Safely handle vitals array
                    if (!Array.isArray(vitals) || vitals.length === 0) {
                        return null;
                    }

                    // Filter vitals safely
                    const filteredVitals = vitals?.filter(vital => {
                        // Skip if vital is null/undefined or missing required properties
                        if (!vital || typeof vital !== 'object') {
                            return false;
                        }

                        // Skip First Primary vital since it's hardcoded above
                        if (vital.name === "First Primary" || vital.field_id === -2) {
                            return false;
                        }

                        // Show vitals with null case_type_id (universal vitals)
                        if (vital.case_type_id === null || vital.case_type_id === undefined) {
                            return true;
                        }

                        // Show vitals that match the current case type
                        const vitalCaseType = parseInt(vital.case_type_id);
                        const currentCaseType = parseInt(leadData?.case_type_id);
                        
                        // Only match if both are valid numbers
                        if (isNaN(vitalCaseType) || isNaN(currentCaseType)) {
                            return false;
                        }
                        
                        return vitalCaseType === currentCaseType;
                    });

                    // Deduplicate using a simpler approach
                    const seenIds = new Set();
                    const uniqueVitals = filteredVitals?.filter(vital => {
                        const id = vital?.id || vital?.field_id || vital?.name;
                        if (seenIds.has(id)) {
                            return false;
                        }
                        seenIds.add(id);
                        return true;
                    });

                    // Map to components
                    return uniqueVitals?.map((vital, index) => {
                        // Parse options for the component
                        let options = [];

                        if (vital?.options) {
                            try {
                                options = JSON.parse(vital?.options);
                            } catch (e) {
                                options = [];
                            }
                        }

                        return (
                            <EditableVital
                                key={vital?.id || `vital-${index}`}
                                vitalKey={String(vital?.id || vital?.field_id || vital?.name || `vital-${index}`)}
                                activeVitalKey={activeVitalKey}
                                setActiveVitalKey={setActiveVitalKey}
                                label={vital?.name}
                                value={vital?.value}
                                typeId={vital?.type_id}
                                field={vital?.field_id}
                                table="custom_fields"
                                customVitalName={!vital?.field_id ? vital?.name : null}
                                updateVital={updateVital}
                                formatDate={formatDate}
                                options={options}
                                contactName={vital?.contact_name}
                                users={users}
                                onCreateNewContact={(callback) => {
                                    if (callback && typeof callback === 'function') {
                                        window.contactCreationCallback = callback;
                                    }
                                    // Note: setCreateContact would need to be passed as prop or handled differently
                                }}
                            />
                        );
                    });
                })()}
            </div>
        </div>
    );
};
